import React, { useState } from 'react';
import { useSelector } from 'react-redux';

const RateInputForm = ({ onSubmit, onCancel, loading, initialData = {} }) => {
  const [formData, setFormData] = useState({
    rates: {
      GOLD: {
        baseRate: initialData.GOLD?.baseRate || '',
        sellMargin: initialData.GOLD?.sellMargin || 2,
        buyMargin: initialData.GOLD?.buyMargin || 2
      },
      SILVER: {
        baseRate: initialData.SILVER?.baseRate || '',
        sellMargin: initialData.SILVER?.sellMargin || 3,
        buyMargin: initialData.SILVER?.buyMargin || 3
      },
      PLATINUM: {
        baseRate: initialData.PLATINUM?.baseRate || '',
        sellMargin: initialData.PLATINUM?.sellMargin || 2.5,
        buyMargin: initialData.PLATINUM?.buyMargin || 2.5
      }
    },
    notes: '',
    requireApproval: false
  });

  const calculateRates = (baseRate, sellMargin, buyMargin) => {
    const base = parseFloat(baseRate) || 0;
    return {
      sellRate: base + (base * sellMargin / 100),
      buyRate: base - (base * buyMargin / 100)
    };
  };

  const handleMetalChange = (metal, field, value) => {
    setFormData(prev => ({
      ...prev,
      rates: {
        ...prev.rates,
        [metal]: {
          ...prev.rates[metal],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const processedData = {
      rates: {},
      notes: formData.notes,
      requireApproval: formData.requireApproval
    };

    Object.keys(formData.rates).forEach(metal => {
      const metalData = formData.rates[metal];
      if (metalData.baseRate) {
        const calculated = calculateRates(
          metalData.baseRate,
          metalData.sellMargin,
          metalData.buyMargin
        );
        
        processedData.rates[metal] = {
          baseRate: parseFloat(metalData.baseRate),
          sellRate: calculated.sellRate,
          buyRate: calculated.buyRate,
          source: 'MANUAL'
        };
      }
    });

    onSubmit(processedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.keys(formData.rates).map(metal => {
          const metalData = formData.rates[metal];
          const calculated = calculateRates(
            metalData.baseRate,
            metalData.sellMargin,
            metalData.buyMargin
          );

          return (
            <div key={metal} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-bold text-lg mb-4 flex items-center">
                {metal === 'GOLD' && '🥇'}
                {metal === 'SILVER' && '🥈'}
                {metal === 'PLATINUM' && '💎'}
                <span className="ml-2">{metal}</span>
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Rate (₹/gm)
                  </label>
                  <input
                    type="number"
                    value={metalData.baseRate}
                    onChange={(e) => handleMetalChange(metal, 'baseRate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter base rate"
                    step="0.01"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Sell Margin (%)
                    </label>
                    <input
                      type="number"
                      value={metalData.sellMargin}
                      onChange={(e) => handleMetalChange(metal, 'sellMargin', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Buy Margin (%)
                    </label>
                    <input
                      type="number"
                      value={metalData.buyMargin}
                      onChange={(e) => handleMetalChange(metal, 'buyMargin', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      step="0.1"
                    />
                  </div>
                </div>

                {metalData.baseRate && (
                  <div className="pt-2 border-t border-gray-200 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sell Rate:</span>
                      <span className="font-semibold">₹{calculated.sellRate.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Buy Rate:</span>
                      <span className="font-semibold">₹{calculated.buyRate.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes (Optional)
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Add any notes about this rate change..."
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="requireApproval"
            checked={formData.requireApproval}
            onChange={(e) => setFormData(prev => ({ ...prev, requireApproval: e.target.checked }))}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="requireApproval" className="ml-2 text-sm text-gray-700">
            Require senior approval for this rate change
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {loading && (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          Set Rates
        </button>
      </div>
    </form>
  );
};

export default RateInputForm;